/**
 * dataService.js — Production implementation (A4a)
 *
 * Loads curated JSON from public/data/.
 * In-memory cache with configurable TTL.
 * Error handling with retry (up to 2 retries).
 * Future API swap: when VITE_USE_MOCK_API=false, switch to live endpoints.
 *
 * Source patterns: Catalog data loading + scaffold stub
 * All data files sourced from Track Overview / Contract Service Excellence app
 */

const USE_MOCK = import.meta.env.VITE_USE_MOCK_API !== 'false';
// D6a (2026-08-24). No TTL on the in-memory data cache — estate offline-first
// doctrine: user-controlled refresh, the "data pulled on" date is the day the
// user pulled. A 30-minute TTL forced a silent re-fetch mid-session that a
// user with a mine-site connection could not see or control. The cache now
// lives for the page session; a page reload = a fresh pull. Matches DTH VC
// and RVC. Adopted estate-wide 2026-08-19.
const MAX_RETRIES = 2;
const RETRY_DELAY = 1000; // 1 second

// U2 Piece A Leaf 1c Rule 14 Step 2 (2026-07-14 late) — data-source toggle.
// VITE_MRT_DATA_SOURCE selects which public/data/*.json set the loader reads:
//   'static'              → /data/*.json           (Excel-built; legacy static)
//   'admin-api-generated' → /data/from-admin-api/*.json (admin-api SQL-generated; DEFAULT)
// Both sets coexist under public/data/ so the toggle is a URL-prefix flip only.
// No behavioural change to caching, retry, or downstream consumers.
// Default flipped to admin-api-generated 2026-07-14 late per Rule 14 Step 3
// coordination — deploy now uses admin-api-shaped JSONs unless env overrides.
const DATA_SOURCE = import.meta.env.VITE_MRT_DATA_SOURCE || 'admin-api-generated';
const DATA_BASE = DATA_SOURCE === 'admin-api-generated' ? '/data/from-admin-api' : '/data';
export const getDataSourceMode = () => DATA_SOURCE;

// In-memory cache: Map<key, { data, expiry }>
const cache = new Map();

// Module-level cache for product availability (large, loaded async at boot).
// Synchronous lookups (getAvailability et al.) read from this; if null they
// degrade gracefully to null/false so guest fallbacks still work.
let _availabilityCache = null;

/**
 * Sleep helper for retry delays
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fetch with retry logic
 * @param {string} url - URL to fetch
 * @param {number} retries - Remaining retry count
 * @returns {Promise<any>} - Parsed JSON response
 */
async function fetchWithRetry(url, retries = MAX_RETRIES) {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText} for ${url}`);
    }
    return await response.json();
  } catch (error) {
    if (retries > 0) {
      console.warn(`[dataService] Retrying ${url} (${retries} left):`, error.message);
      await sleep(RETRY_DELAY);
      return fetchWithRetry(url, retries - 1);
    }
    throw error;
  }
}

/**
 * Load data with caching. No TTL — see D6a comment at the top of the file.
 * Once fetched during a page session, subsequent calls return the cached
 * copy until reload.
 */
async function loadWithCache(key, url) {
  const cached = cache.get(key);
  if (cached) return cached.data;
  const data = await fetchWithRetry(url);
  cache.set(key, { data });
  return data;
}

// ============================================================================
// PUBLIC API — Product Data
// ============================================================================

export async function loadProducts(domain) {
  return loadWithCache(`products-${domain}`, `${DATA_BASE}/products-${domain}.json`);
}

export async function loadAllProducts() {
  const domains = ['tophammer', 'dth', 'rotary', 'rotary-bits', 'raiseboring', 'resharpening'];
  const results = await Promise.allSettled(
    domains.map(d => loadProducts(d))
  );
  const allProducts = [];
  results.forEach((result, i) => {
    if (result.status === 'fulfilled') {
      const envelope = result.value;
      const products = Array.isArray(envelope) ? envelope : (envelope.products || []);
      products.forEach(p => {
        allProducts.push({ ...p, _domain: domains[i] });
      });
    } else {
      console.warn(`[dataService] Failed to load ${domains[i]}:`, result.reason);
    }
  });
  return allProducts;
}

export async function getProductByItemCode(itemCode) {
  const allProducts = await loadAllProducts();
  return allProducts.find(p => p.itemCode === itemCode) || null;
}

// ============================================================================
// PUBLIC API — Reference Data
// ============================================================================

export async function loadCategories() {
  return loadWithCache('categories', `${DATA_BASE}/categories.json`);
}

export async function loadRigs() {
  return loadWithCache('rigs', `${DATA_BASE}/rigs.json`);
}

export async function loadSalesAreas() {
  return loadWithCache('salesAreas', `${DATA_BASE}/sales-areas.json`);
}

export async function loadRecommendations() {
  try {
    return await loadWithCache('recommendations', `${DATA_BASE}/recommendations.json`);
  } catch {
    console.warn('[dataService] recommendations.json not available yet');
    return { recommendations: [] };
  }
}

export async function loadCompetitiveXref() {
  try {
    return await loadWithCache('competitiveXref', `${DATA_BASE}/competitive-xref.json`);
  } catch {
    console.warn('[dataService] competitive-xref.json not available yet');
    return { mappings: [] };
  }
}

/**
 * Load bit selection guide (ground conditions → grade)
 * Note: bit-selection-guide.json is also bundled via src/data/ for the
 * synchronous BIT_SELECTION_GUIDE export. This helper remains for any
 * code that prefers the async fetch pattern.
 */
export async function loadBitSelectionGuide() {
  try {
    return await loadWithCache('bitSelectionGuide', `${DATA_BASE}/bit-selection-guide.json`);
  } catch {
    console.warn('[dataService] bit-selection-guide.json not available yet');
    return { guide: [] };
  }
}

/**
 * Load per-(item × sales-entity) product availability (large, ~9 MB).
 * Populates the module-level cache used by the synchronous availability
 * lookups below. Call at app boot via preloadCriticalData().
 */
export async function loadProductAvailability() {
  try {
    const result = await loadWithCache('productAvailability', `${DATA_BASE}/product-availability.json`);
    _availabilityCache = result;
    return result;
  } catch (err) {
    console.warn('[dataService] product-availability.json not available:', err?.message);
    const fallback = { availability: {} };
    _availabilityCache = fallback;
    return fallback;
  }
}

export async function loadContent() {
  try {
    return await loadWithCache('content', `${DATA_BASE}/content.json`);
  } catch {
    console.warn('[dataService] content.json not available yet');
    return { pages: [] };
  }
}

export async function loadRolesFeatures() {
  try {
    return await loadWithCache('rolesFeatures', `${DATA_BASE}/roles-features.json`);
  } catch {
    console.warn('[dataService] roles-features.json not available yet');
    return { roles: [], features: [] };
  }
}

// ============================================================================
// Cache Management
// ============================================================================

export function clearCache() {
  cache.clear();
}

export function invalidateCache(key) {
  cache.delete(key);
}

export function getCacheStatus() {
  const status = {};
  cache.forEach((value, key) => {
    status[key] = {
      hasData: !!value.data,
      expiresIn: Math.max(0, Math.round((value.expiry - Date.now()) / 1000)) + 's',
      isExpired: Date.now() >= value.expiry,
    };
  });
  return status;
}

// ============================================================================
// Preloader — call on app init to warm the cache
// ============================================================================

export const getAllProducts = loadAllProducts;

export async function preloadCriticalData() {
  try {
    await Promise.allSettled([
      loadCategories(),
      loadProducts('tophammer'),
      loadProducts('dth'),
      loadProductAvailability(),
    ]);
    console.info('[dataService] Critical data preloaded');
  } catch (err) {
    console.warn('[dataService] Preload partial failure:', err);
  }
}

// ============================================================================
// PUBLIC API — Product Availability (per-item x sales-entity)
// ============================================================================
// Synchronous lookups against the module-level _availabilityCache populated
// by loadProductAvailability() at app boot. Until the cache is hydrated, all
// helpers return null/false and consumers fall through to static product.pics
// (the guest-fallback path designed into useAvailability).

/**
 * Raw per-(item x entity) availability lookup.
 * @returns {{sellable: boolean, display: boolean, pics: string} | null}
 */
export function getAvailability(itemCode, entityCode) {
  if (!itemCode || !entityCode) return null;
  return _availabilityCache?.availability?.[itemCode]?.[entityCode] || null;
}

/**
 * Test-only seeder for the module-level availability cache.
 * Production code MUST use loadProductAvailability() at boot instead.
 * Pass null to reset.
 * @internal
 */
export function __setAvailabilityCacheForTests(payload) {
  _availabilityCache = payload;
}

/**
 * Effective PICS lifecycle code. Per-entity if known, else static fallback.
 */
export function getEffectivePics(product, entityCode) {
  const perEntity = getAvailability(product?.itemCode, entityCode);
  if (perEntity?.pics) return perEntity.pics;
  return product?.pics || null;
}

/** Master visibility gate. */
export function isDisplayable(itemCode, entityCode) {
  return getAvailability(itemCode, entityCode)?.display === true;
}

/** Sellable for direct order in this region. */
export function isSellable(itemCode, entityCode) {
  return getAvailability(itemCode, entityCode)?.sellable === true;
}

/**
 * Filter a product array by a "Show only" view (Available / By request / Older / All).
 * Master gate: `display === true`. The chosen view then partitions by PICS:
 *   - 'available' (default): display && sellable && pics === 'S'
 *   - 'byRequest':           display && pics === 'N'
 *   - 'older':               display && pics === 'U'
 *   - 'all':                 display === true (any PICS)
 *
 * Guest fallback (no entityCode / no per-entity record): we treat the static
 * `product.pics` as the source of truth and assume display=true. The view
 * still partitions by PICS so the dropdown remains useful for unauthenticated
 * traffic. See Group D brainstorm §8.1 + §8.3.
 *
 * @param {Object[]} products
 * @param {'available'|'byRequest'|'older'|'all'} view
 * @param {string|null} entityCode
 * @returns {Object[]}
 */
export function filterByPicsView(products, view, entityCode) {
  if (!Array.isArray(products)) return [];
  return products.filter((p) => {
    const a = getAvailability(p?.itemCode, entityCode);

    // Master visibility gate. If per-entity record exists, honour its
    // display flag strictly; if no record (guest / unknown entity), fall
    // through and use the static `pics` view-only partitioning.
    if (a) {
      if (a.display !== true) return false;
    }

    const pics = getEffectivePics(p, entityCode);
    switch (view) {
      case 'available':
        // sellable check only enforced when we actually have a per-entity
        // record; guests with no record default to "assume sellable".
        return (a ? a.sellable === true : true) && pics === 'S';
      case 'byRequest':
        return pics === 'N';
      case 'older':
        return pics === 'U';
      case 'all':
        return true;
      default:
        return true;
    }
  });
}

// ============================================================================
// PUBLIC API — ToolFinder bundle loader (Sub-Plan 3 D7/D9)
// ----------------------------------------------------------------------------
// Mirrors the TF v4 dataService surface (see
// `Top Hammer Toolfinder/tophammer-toolfinder-v4/src/services/dataService.js`,
// 17 May 2026) so the ported ProductContext can call a single loadAllData()
// instead of hand-wiring fetches.
//
// Behavioural differences from TF v4:
//   - MRT products live in two envelopes (`products-tophammer.json` flat
//     {products[], categories[]} + `products-dth.json` same shape) instead
//     of TF v4's pre-grouped `products.json`. We GROUP them here:
//       * TH → keyed by `subcategory`  (TF-style; matches MASTER_TAB_REGISTRY).
//       * DTH → keyed by `category`    (DTH products carry `category`, not
//         `subcategory`; audit 17 May 2026).
//   - `productsDTH` is a new MRT-only key in the return envelope.
//   - `categoryConfig.json` may not exist yet; we try-catch and return null.
// ============================================================================

/**
 * Load rock drills JSON ({rockDrills:{surface,underground}, ...}).
 * Matches TF v4 envelope (see TF v4 dataService.js loadRockDrills).
 */
export async function loadRockDrills() {
  return loadWithCache('rockDrills', `${DATA_BASE}/rockDrills.json`);
}

/**
 * Load rock drill manufacturers JSON (Phase 4.7 in TF v4).
 * Envelope: { version, lastUpdated, manufacturers:[{id, name, models:[]}] }.
 */
export async function loadRockDrillManufacturers() {
  return loadWithCache('rockDrillManufacturers', `${DATA_BASE}/rockDrillManufacturers.json`);
}

/**
 * Load category-config JSON if present. Optional — many MRT environments
 * have not generated this asset yet, so we degrade to null on miss.
 */
export async function loadCategoryConfig() {
  try {
    return await loadWithCache('categoryConfig', `${DATA_BASE}/categoryConfig.json`);
  } catch (err) {
    console.warn('[dataService] categoryConfig.json not available:', err?.message);
    return null;
  }
}

/**
 * Group a flat products array by a string field, returning an
 * `{ [key]: product[] }` object. Falsy keys / non-strings are skipped.
 *
 * @param {Array<Object>} products
 * @param {string} keyField — 'subcategory' for TH, 'category' for DTH.
 * @returns {Object<string, Array<Object>>}
 */
export function groupProductsByField(products, keyField) {
  const result = {};
  if (!Array.isArray(products)) return result;
  for (const p of products) {
    const key = p && p[keyField];
    if (!key || typeof key !== 'string') continue;
    if (!result[key]) result[key] = [];
    result[key].push(p);
  }
  return result;
}

/**
 * loadAllData — Sub-Plan 3 ToolFinder bundle.
 *
 * Mirrors TF v4 `loadAllData` and adds the MRT-specific `productsDTH` key.
 * Uses `Promise.allSettled` so a single bad fetch doesn't break the page;
 * a failed dataset surfaces as `null` and the consumer can render a partial
 * UI (matching TF v4 dataService.js behaviour).
 *
 * @returns {Promise<{
 *   products: Object<string, Array>|null,
 *   productsDTH: Object<string, Array>|null,
 *   productsRotary: Object<string, Array>|null,
 *   rigs: Object|null,
 *   rockDrills: Object|null,
 *   rockDrillManufacturers: Object|null,
 *   categoryConfig: Object|null,
 * }>}
 */
export async function loadAllData() {
  const results = await Promise.allSettled([
    loadProducts('tophammer'),          // 0 — TH envelope
    loadProducts('dth'),                // 1 — DTH envelope
    loadRigs(),                         // 2
    loadRockDrills(),                   // 3
    loadRockDrillManufacturers(),       // 4
    loadCategoryConfig(),               // 5 — optional, may resolve to null
    loadProducts('rotary'),             // 6 — Rotary drill-pipes envelope
    loadProducts('rotary-bits'),        // 7 — Rotary bits envelope (Group R1)
  ]);

  const thEnvelope        = results[0].status === 'fulfilled' ? results[0].value : null;
  const dthEnvelope       = results[1].status === 'fulfilled' ? results[1].value : null;
  const rigs              = results[2].status === 'fulfilled' ? results[2].value : null;
  const rockDrills        = results[3].status === 'fulfilled' ? results[3].value : null;
  const rockDrillMfrs     = results[4].status === 'fulfilled' ? results[4].value : null;
  const categoryConfig    = results[5].status === 'fulfilled' ? results[5].value : null;
  const rotaryEnvelope    = results[6].status === 'fulfilled' ? results[6].value : null;
  const rotaryBitsEnv     = results[7].status === 'fulfilled' ? results[7].value : null;

  // Log failures (helpful in dev + visible to Sentry adapters if present).
  const names = ['products-tophammer', 'products-dth', 'rigs', 'rockDrills', 'rockDrillManufacturers', 'categoryConfig', 'products-rotary', 'products-rotary-bits'];
  results.forEach((r, i) => {
    if (r.status === 'rejected') {
      console.warn(`[dataService.loadAllData] ${names[i]} failed:`, r.reason?.message || r.reason);
    }
  });

  // TH products: group flat {products[], categories[]} by `subcategory`.
  const thProducts = thEnvelope
    ? groupProductsByField(Array.isArray(thEnvelope) ? thEnvelope : (thEnvelope.products || []), 'subcategory')
    : null;

  // DTH products: group flat {products[], categories[]} by `category`.
  const dthProducts = dthEnvelope
    ? groupProductsByField(Array.isArray(dthEnvelope) ? dthEnvelope : (dthEnvelope.products || []), 'category')
    : null;

  // Rotary products (drill-pipes + bits): merge both envelopes then group by
  // `subcategory`. Bits carry productLine="rotary" and their own subcategory
  // slugs (rotary-bits-rr240 etc), so they slot in cleanly alongside pipes.
  const rotaryArr    = rotaryEnvelope
    ? (Array.isArray(rotaryEnvelope) ? rotaryEnvelope : (rotaryEnvelope.products || []))
    : [];
  const rotaryBitsArr = rotaryBitsEnv
    ? (Array.isArray(rotaryBitsEnv) ? rotaryBitsEnv : (rotaryBitsEnv.products || []))
    : [];
  const rotaryMerged = [...rotaryArr, ...rotaryBitsArr];
  const rotaryProducts = rotaryMerged.length
    ? groupProductsByField(rotaryMerged, 'subcategory')
    : null;

  return {
    products: thProducts,
    productsDTH: dthProducts,
    productsRotary: rotaryProducts,
    rigs,
    rockDrills,
    rockDrillManufacturers: rockDrillMfrs,
    categoryConfig,
  };
}
